import axios, { AxiosRequestTransformer } from "axios";
import {
  API_BASE_URL,
  ASSUME_ROLE_ENABLED,
  API_AD_APP_SCOPE,
} from "@/helpers/config";
import { useAuthStore } from "@/stores/auth";
import { useUiStore } from "@/stores/ui";
import { storeToRefs } from "pinia";
import { DATE_TIME_FORMAT, formatDateTimeToMasked } from "@/helpers/date";

/**
 * The original developers of TDP made the absurd decision to have every date time be in NZST, an
 * for the API not to support offsets. Javascript date sends as iso (UTC) by default, which wouldn't be a problem
 * if the api had been implemented competently. Unfortunately it was not, so this hack is necessary.
 *
 * This basically recurses the payload, finds any JavaScript dates, and formats them in a standard format. Pretty gross
 * fix, but about the best we can do.
 *
 * Someone should fix the api, but for now this should do the trick.
 *
 * @param data the data to be trasferomed.
 * @returns The data with any dates converted to local time.
 */
const dateTransformer = (data: unknown) => {
  if (data instanceof Date) {
    return formatDateTimeToMasked(data, DATE_TIME_FORMAT);
  }
  if (Array.isArray(data)) {
    return data.map(dateTransformer);
  }
  if (typeof data === "object" && data !== null) {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, dateTransformer(value)]),
    );
  }
  return data;
};

export const http = axios.create({
  baseURL: API_BASE_URL,
  transformRequest: [
    dateTransformer,
    ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
  ],
});

let pendingHttpRequests = 0;
function showOrHideLoadingIndicator(show) {
  if (show) pendingHttpRequests++;
  else pendingHttpRequests--;
  if (!show && pendingHttpRequests > 0) return;

  const $uiStore = useUiStore();
  const { isLoading } = storeToRefs($uiStore);
  isLoading.value = show;
}

// get an access token before each request
http.interceptors.request.use(async (config) => {
  config.headers["x-api-frontend-version"] = import.meta.env.PACKAGE_VERSION;

  const $authStore = useAuthStore();
  const { assumedRole } = storeToRefs($authStore);

  if (ASSUME_ROLE_ENABLED) {
    if (assumedRole.value != undefined) {
      config.headers["Assume-Role"] = assumedRole.value;
    }
  }

  await $authStore.initializeAuthentication();

  return $authStore.acquireTokenRedirect(API_AD_APP_SCOPE).then(
    (token) => {
      config.headers.Authorization = `Bearer ${token.accessToken}`;
      showOrHideLoadingIndicator(true);
      return config;
    },
    (err) => {
      console.error(err);
      return err;
    },
  );
});

http.interceptors.response.use(
  function (response) {
    showOrHideLoadingIndicator(false);
    return response;
  },
  function (error) {
    showOrHideLoadingIndicator(false);
    return Promise.reject(error);
  },
);
